import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { Observable, Subscription, combineLatest } from 'rxjs';
import { switchMap, filter, map, take } from 'rxjs/operators';
import { isString } from 'lodash';

import { ChipContainerComponent } from '../../../../Common/Forms/components/ChipContainer/chip-container.component';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import {
    AppearCondition,
    AutocompleteCustomOption
} from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { TagModel } from '../../../../Common/Tags/models/tag.model';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { FolderService } from '../../../../Monitor/Core/services/folder.service';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { SocialSearchService } from '../../../Core/services/social-search.service';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';
import { ResourceActionTypes } from '../../../../Common/Basic/enums/resource-actions.enum';

interface SaveSettings {
    folderId?: number;
    tagIds?: number[];
}

@Component({
    selector: 'agpr-social-post-save-tool',
    templateUrl: './social-post-save-tool.component.html',
    styleUrls: ['./social-post-save-tool.component.scss'],
    standalone: false
})
export class SocialPostSaveToolComponent implements OnInit {
    @Input() id: string;
    @Input() selectedMentions: Mention[] = [];
    @Input() disabled: boolean = false;

    @Output() folderChanged = new EventEmitter<Folder>();

    @ViewChild('tagChipsComponent') tagChipsComponent: ChipContainerComponent;

    showSaveMenu: boolean = false;

    folderSettingsLocked: boolean = false;
    tagSettingsLocked: boolean = false;

    selectedFolder: Folder = null;
    selectedTags: TagModel[] = [];

    tagChips: { label: string; tag: TagModel }[] = [];

    saveButtonTooltip: string = '';
    search: MentionSearchModel;

    private tagList: TagModel[] = [];
    private folderList: Folder[] = [];

    private subscriptions: Subscription[] = [];
    private actionBeingPerformed: Subscription;

    readonly buttonTypes = ButtonTypesEnum;
    readonly agprComponentStyleEnum = AgprComponentStyleEnum;
    readonly saveSettingsSessionKey: string = 'agpr-socialSaveSettings';
    readonly folderAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Mention.editDialog.folder.createNewFolder',
            action: (folderName: string) => {
                const folder = new Folder();
                folder.name = folderName;

                this.folderService.create(folder).pipe(
                    switchMap(id => this.folderService.all$.pipe(
                        map(folders => folders.find(x => x.id === id))
                    )),
                    take(1)
                ).subscribe(newFolder => {
                    this.selectedFolder = newFolder;
                    this.folderChanged.next(this.selectedFolder);
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];
    readonly tagsAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Tags.createTag',
            action: (searchString: string) => {
                const tagModel = new TagModel();
                tagModel.name = searchString;

                this.tagsService.createTag(tagModel).pipe(
                    take(1),
                    switchMap((id) => this.tagsService.getTagById(id))
                ).subscribe(tag => {
                    this.selectedTags.push(tag);
                    this.updateTagsListState();
                    this.updateLockedSaveSettings();
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    constructor(
        public tagsService: TagsService,
        public mentionsService: MentionsService,
        public folderService: FolderService,
        public localizationService: LocalizationService,
        private socialSearchService: SocialSearchService,
    ) { }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.folderService.all$,
                this.tagsService.all$
            ]).subscribe(([folders, tags]) => {
                this.folderList = folders || [];
                this.tagList = tags;

                this.getLockedSaveSettings();
            }),
            this.socialSearchService.getSearchCriteria().subscribe(
                searchCriteria => {
                    this.search = new MentionSearchModel();
                    this.search.searchCriteria = searchCriteria;
                }
            ),
            this.socialSearchService.getMentions().subscribe(
                mentions => {
                    this.mentionsService.setMentions(mentions);
                }
            ),
        );
    }

    /**
     * Gets the locked save settings from the session
     * [dropdown folder and tags for saving mentions]
     */
    private getLockedSaveSettings() {
        const settingsString: string = sessionStorage.getItem(this.saveSettingsSessionKey);

        if (!settingsString) {

            return;
        }

        const settings: SaveSettings = JSON.parse(settingsString);

        if (settings.folderId) {
            this.folderSettingsLocked = true;
            this.selectedFolder = this.folderList.find(folder => folder.id === settings.folderId) || null;
            this.folderChanged.next(this.selectedFolder);
        }

        if (settings.tagIds?.length > 0) {
            this.tagSettingsLocked = true;
            this.selectedTags = this.tagList.filter(tag => settings.tagIds.includes(tag.id)) || [];

            this.updateTagsListState();
            this.setSaveButtonTooltip();
        }
    }

    /**
     * Updates the state of the tag selection
     */
    updateTagsListState(): void {
        if (this.selectedTags) {
            this.tagChips = this.selectedTags.map((tag) => {
                return {
                    label: tag.name,
                    tag: tag
                };
            });
        }
    }

    /**
     * Updates the session storage for the save settings
     */
    updateLockedSaveSettings(): void {
        if (!this.tagSettingsLocked && !this.folderSettingsLocked) {
            sessionStorage.removeItem(this.saveSettingsSessionKey);
            return;
        }

        const settings: SaveSettings = {
            tagIds: null,
            folderId: null
        };

        if (this.tagSettingsLocked) {
            settings.tagIds = this.selectedTags.map(tag => tag.id);
        }
        if (this.folderSettingsLocked && this.selectedFolder) {
            settings.folderId = this.selectedFolder.id;
        }

        sessionStorage.setItem(this.saveSettingsSessionKey, JSON.stringify(settings));
    }

    toggleFolderLock() {
        this.folderSettingsLocked = !this.folderSettingsLocked;
        this.updateLockedSaveSettings();
    }

    toggleTagLock() {
        this.tagSettingsLocked = !this.tagSettingsLocked;
        this.updateLockedSaveSettings();
    }

    /**
     * Selects a tag to be used when saving mentions
     */
    selectTag() {
        this.updateTagsListState();
        this.updateLockedSaveSettings();
        this.setSaveButtonTooltip();
    }

    /**
     * Selects a folder to be used when saving mentions
     */
    selectFolder() {
        this.updateLockedSaveSettings();
        this.setSaveButtonTooltip();
        this.folderChanged.next(this.selectedFolder);
    }

    clearFolder(): void {
        this.selectedFolder = null;
        this.folderChanged.next(this.selectedFolder);
    }

    /**
     * Removes a tag from the list to be used when saving mentions
     * @param index Index of the chip being removed
     */
    unselectTagChip(index: number): void {
        const removeTag: TagModel = this.tagChips[index].tag;
        this.selectedTags = this.selectedTags.filter((tag) => tag !== removeTag);
        this.updateTagsListState();
        this.updateLockedSaveSettings();
        this.setSaveButtonTooltip();
    }

    /**
     * Resets the save settings
     * @param force Forces a clear even if locked
     */
    resetSettings(force: boolean = false): void {
        if (this.folderSettingsLocked && this.tagSettingsLocked && !force) {
            return;
        }

        this.selectedFolder = null;
        this.selectedTags = [];
        this.updateTagsListState();
        this.setSaveButtonTooltip();
    }

    /**
     * Clear the current Saved Settings
     */
    clearSettings() {
        this.tagChipsComponent.chipPopover.hide();
        this.resetSettings(true);
        this.folderSettingsLocked = false;
        this.tagSettingsLocked = false;
        this.updateLockedSaveSettings();
    }

    savePost() {
        if (this.disabled || this.selectedMentions.length <= 0) {
            return;
        }
        if (this.selectedFolder) {
            this.saveMentionsToFolder();
        } else {
            this.saveMentionsToAccount();
        }
    }


    /**
     * Saves the selected mentions to the currently selected folder
     */
    private saveMentionsToFolder() {
        if (this.actionBeingPerformed) {
            return;
        }

        const folder: Folder = this.selectedFolder;
        if (!folder || folder.id < 0) {
            return;
        }

        const addTags = this.selectedTags && this.selectedTags.length > 0;
        const request = this.folderService.saveMentionsToFolder(
            this.selectedMentions,
            folder,
            !addTags,
            this.search,
            this.selectedTags
        );
        this.setActionBeingPerformed(request);
    }

    /**
     * Saves the selected mentions to the current account
     */
    private saveMentionsToAccount() {
        if (this.actionBeingPerformed) {
            return;
        }

        const addTags = this.selectedTags && this.selectedTags.length > 0;
        const request = this.mentionsService.saveMentionsToAccount(
            this.selectedMentions,
            !addTags,
            this.search,
            this.selectedTags
        );

        this.setActionBeingPerformed(request);
    }

    /**
     * Stores an action that is being performed so that we can prevent more actions until the current one is complete
     */
    private setActionBeingPerformed(action: Observable<any>): void {
        this.actionBeingPerformed = action.subscribe({
            next: () => {
                if (this.actionBeingPerformed) {
                    this.actionBeingPerformed.unsubscribe();
                    this.actionBeingPerformed = null;
                }
            },
            error: () => {
                if (this.actionBeingPerformed) {
                    this.actionBeingPerformed.unsubscribe();
                }
                this.actionBeingPerformed = null;
            }
        });
    }

    private setSaveButtonTooltip() {
        let tooltipHtml = '';
        if (!this.selectedFolder && this.selectedTags.length <= 0) {
            this.saveButtonTooltip = this.localizationService.transform('Social.saveTool.saveToAccount');
        } else {
            tooltipHtml = `<div class="agpr-socialPostSaveTool-saveTooltip"> ${this.localizationService.transform('Social.saveTool.savePost')}`;
            if (this.selectedFolder) {
                // eslint-disable-next-line max-len
                tooltipHtml += `<div> <span style="font-weight:bold"> ${this.localizationService.transform('Social.saveTool.folder')}: </span> ${this.selectedFolder.name}</div>`;
            }
            if (this.selectedTags.length > 0) {
                const tagNames = this.selectedTags.map(tag => tag.name).toString().replace(/,/g, ', ');
                // eslint-disable-next-line max-len
                tooltipHtml += `<div> <span style="font-weight:bold"> ${this.localizationService.transform('Social.saveTool.tags')}: </span> ${tagNames} </div>`;
            }
            tooltipHtml += '</div>';
            this.saveButtonTooltip = tooltipHtml;
        }

    }

    openSaveMenu() {
        this.showSaveMenu = !this.showSaveMenu;
    }

    closeSaveMenu(source?: any) {
        // Exclude doesn't seem to work, so this is a manual exclude, specifically for removing the tag chips
        if (source?.path) {
            for (const pathElement of source.path) {
                if (pathElement.className &&
                    isString(pathElement.className) &&
                    pathElement.className.indexOf('agpr-clickOutsideExcluded') !== -1) {
                    return;
                }
            }
        }
        this.showSaveMenu = false;
    }
}
